import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { BlueprintChain } from "../components/BlueprintChain";
import { Marquee } from "../components/Marquee";
import { Reveal, Stagger, Item } from "../components/Reveal";
import { scrollToId } from "../components/SmoothScroll";

const HERO_LINES = [
  [{ t: "Proof for every decision" }],
  [{ t: "your " }, { t: "AI", hl: true }, { t: " makes." }],
];

const MaskedHeading = () => (
  <h1 data-testid="hero-heading">
    {HERO_LINES.map((line, li) => (
      <span className="line-mask" key={li}>
        <motion.span style={{ display: "inline-block" }}
          initial={{ y: "110%" }} animate={{ y: "0%" }}
          transition={{ duration: 0.9, delay: 0.15 + li * 0.12, ease: [0.22, 1, 0.36, 1] }}>
          {line.map((s, si) => s.hl ? <span className="hl" key={si}>{s.t}</span> : <span key={si}>{s.t}</span>)}
        </motion.span>
      </span>
    ))}
  </h1>
);

const fade = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

export default function Landing() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) setTimeout(() => scrollToId(hash), 300);
  }, []);

  return (
    <main>
      {/* HERO */}
      <section className="hero" id="top">
        <div className="wrap hero-grid">
          <div>
            <motion.div className="tagline" {...fade} transition={{ duration: 0.6 }}>
              <span className="b" /><span className="c">Verify.</span> <span className="c">Authenticate.</span> <span className="g">Trust every AI process.</span>
            </motion.div>
            <MaskedHeading />
            <motion.p className="lede" {...fade} transition={{ duration: 0.7, delay: 0.55 }}>
              VeriAuth attests what your AI took in, how it decided, and what it produced — into signed,
              tamper-evident records that anyone can verify <em>without trusting the operator</em>. One engine. Any model.
              Any domain.
            </motion.p>
            <motion.div className="cta-row" {...fade} transition={{ duration: 0.6, delay: 0.7 }}>
              <a className="btn" href="/#test" data-testid="hero-cta-test" onClick={(e) => { e.preventDefault(); scrollToId("test"); }}>Test your AI in 2 minutes →</a>
              <a className="btn ghost" href="/demo" data-testid="hero-cta-demo" onClick={(e) => { e.preventDefault(); navigate("/demo"); }}>Try the live demo</a>
            </motion.div>
            <motion.div className="trust" {...fade} transition={{ duration: 0.6, delay: 0.85 }}>
              <b>Independently verifiable</b> · hardware-signed · append-only · <b>you can't fake it, and neither can we</b>
            </motion.div>
          </div>
          <BlueprintChain />
        </div>
      </section>

      <Marquee />

      {/* PROBLEM */}
      <section id="problem">
        <div className="wrap">
          <Reveal className="sec-head">
            <div className="eyebrow">The problem</div>
            <h2>A self-attested log proves nothing.</h2>
            <p className="lede">AI now makes decisions that affect money, health, eligibility and rights. When one is
              challenged, the operator is asked to prove what actually happened — and can't. A row in a database you
              control is not evidence to anyone who doesn't trust you.</p>
          </Reveal>
          <Stagger className="grid g3" style={{ marginTop: 28 }}>
            {[
              ["01", "Unprovable", "Your logs are written by you and editable by you. In a dispute, audit or court they carry no forensic weight — they're a claim, not proof."],
              ["02", "Unverifiable", "No independent party can confirm which model ran, on what input, to produce which output. \u201cTrust us\u201d is the only assurance on offer."],
              ["03", "Un-backfillable", "Tamper-evident history only exists for decisions attested as they happen. The day you need the proof is the day it's too late to start collecting it."],
            ].map(([n, h, p]) => (
              <Item className="card accent" key={n}>
                <div className="chapter"><span className="num">{n}</span>
                  <div><h3>{h}</h3><p style={{ marginTop: 6 }}>{p}</p></div>
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* CONSEQUENCES */}
      <section id="consequences">
        <div className="wrap">
          <Reveal className="sec-head">
            <div className="eyebrow">The consequences</div>
            <h2>What the gap exposes you to.</h2>
            <p className="lede">Two kinds of risk follow from unprovable AI: concrete attacks that are invisible without
              attestation, and a fast-tightening regulatory expectation to demonstrate what your AI did.</p>
          </Reveal>

          <div className="flowlabel">Threats — invisible without attestation</div>
          <Stagger className="grid g3" style={{ marginTop: 12 }}>
            {[
              ["Repudiation", "A customer or counterparty disputes a decision and you cannot prove what your AI actually did."],
              ["Model-swap", "A tampered or unapproved model is substituted and runs silently. You can't prove which version decided."],
              ["Evidence tampering", "Decision history is deleted or rewritten after the fact — the exact records a regulator would demand."],
              ["Output tampering", "An outcome is altered in transit or at rest with nothing to detect the change."],
              ["Data exfiltration", "Sensitive data flows to an external AI service with no attested control over what was sent or used."],
              ["Examination failure", "Asked for your AI decision trail, you assemble raw logs by hand — the worst posture under scrutiny."],
            ].map(([h, p]) => (
              <Item className="card threat" key={h}><div className="idx">✕</div><h3>{h}</h3><p>{p}</p></Item>
            ))}
          </Stagger>

          <div className="flowlabel">Regulatory — the direction of travel</div>
          <Stagger className="grid g2" style={{ marginTop: 12 }}>
            {[
              ["RBI FREE-AI (India)", "Advisory", false, "The 2025 framework recommends audit trails, model-risk governance and assurance for AI in regulated finance. Advisory — but it signals where supervision is heading, and RBI enforces the underlying obligations through its existing powers."],
              ["DPDP Act 2023 (India)", "Statutory", true, "Fixed, per-violation penalties for failing to protect personal data — up to ₹250 cr (security safeguards) and ₹200 cr (breach notification), imposed by the Data Protection Board."],
              ["EU AI Act", "Statutory", true, "High-risk AI systems carry record-keeping, traceability and post-market monitoring obligations — provable logging of how decisions were produced."],
              ["The global pattern", "Emerging", false, "Across sectors and jurisdictions the same expectation is forming: if an AI decides, you must be able to demonstrate — provably — what it did."],
            ].map(([h, tag, stat, p]) => (
              <Item className="card" key={h}>
                <div className="vhead"><h3>{h}</h3>
                  <span className="vtag" style={stat ? { color: "var(--amber)", borderColor: "var(--redLine)" } : {}}>{tag}</span>
                </div>
                <p style={{ marginTop: 8 }}>{p}</p>
              </Item>
            ))}
          </Stagger>

          <Reveal className="exp">
            <div className="k">On the honest record</div>
            <div className="v">Statutory maximums, discretionary — not automatic fines.</div>
            <div className="fine">DPDP Act penalties are Schedule-capped ceilings under s.33, applied per violation at the
              Board's discretion after inquiry. RBI FREE-AI is advisory and carries no fine of its own. Figures here are
              statutory maximums, not predictions of any specific penalty. This is not legal advice.</div>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how">
        <div className="wrap">
          <Reveal className="sec-head">
            <div className="eyebrow">How we achieve it</div>
            <h2>Attest three points. Seal it. Let anyone verify.</h2>
            <p className="lede">VeriAuth wraps around your AI without changing what it does. It captures the decision at three
              points, binds them into one signed certificate, and writes it to a tamper-evident log that any third party
              can check — with no access to your systems and no trust in you.</p>
          </Reveal>

          <div className="flowlabel">Capture</div>
          <Stagger className="flow">
            {[["Input", "Canonicalize + hash what the AI saw"], ["Processing", "Attest which model / policy ran"], ["Output", "Canonicalize + hash the decision"]].map(([t, s]) => (
              <Item className="node eng" key={t}><div className="t">{t}</div><div className="s">{s}</div></Item>
            ))}
          </Stagger>
          <div className="flowlabel">Seal &amp; store</div>
          <Stagger className="flow">
            <Item className="node eng"><div className="t">Sign certificate</div><div className="s">Hardware-held key · input + model + output + time</div></Item>
            <Item className="node store"><div className="t">Append-only vault</div><div className="s">Chain-hashed — any tampering breaks the chain</div></Item>
          </Stagger>
          <div className="flowlabel">Verify — by anyone</div>
          <Stagger className="flow">
            <Item className="node check"><div className="t">Independent verifier</div><div className="s">Recompute hashes · check signature + chain · no operator trust needed</div></Item>
          </Stagger>

          <Stagger className="grid g3" style={{ marginTop: 30 }}>
            {[
              ["Domain-agnostic core", "The model is treated as opaque; inputs and outputs are plain data. Only a compliance pack is domain-specific. Swap the pack, attest a different domain."],
              ["Consume hardware trust", "Signing binds to an HSM / secure element via standard interfaces. We consume hardware roots of trust — we don't ask you to build them."],
              ["Erase & retain, at once", "Per-subject key-shredding lets you honour a data-erasure request while the tamper-evident proof of the record survives — resolving retention vs. erasure."],
            ].map(([h, p]) => (
              <Item className="card" key={h}><div className="idx">Principle</div><h3>{h}</h3><p>{p}</p></Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* DOMAINS */}
      <section id="domains">
        <div className="wrap">
          <Reveal className="sec-head">
            <div className="eyebrow">Where it applies</div>
            <h2>One engine. Every domain that needs independent verification.</h2>
            <p className="lede">If a party who doesn't trust the operator must rely on an AI outcome, VeriAuth fits. Financial
              services is our live beachhead — not our boundary.</p>
          </Reveal>
          <Stagger className="grid g3" style={{ marginTop: 26 }}>
            {[
              ["Financial services", "Live", true, "Credit, fraud, KYC/AML and collections decisions — attested for disputes, audits and RBI examinations."],
              ["Healthcare", "Ready", false, "Diagnostic and triage recommendations — provable model, inputs and output for clinical accountability."],
              ["Insurance", "Ready", false, "Underwriting and claims decisions — verifiable records for contested outcomes and liability exposure."],
              ["Hiring & HR", "Ready", false, "Automated screening and ranking — an audit-ready trail for fairness and challenge."],
              ["Public sector", "Ready", false, "Benefit eligibility and allocation — attestation citizens and oversight bodies can independently check."],
              ["Agentic AI & tool-use", "Ready", false, "Autonomous agents taking actions — a signed record of what an agent decided and did, per step."],
            ].map(([h, tag, live, p]) => (
              <Item className="card lift" key={h}>
                <div className="vhead"><h3>{h}</h3><span className={"vtag" + (live ? " live" : "")}>{tag}</span></div>
                <p style={{ marginTop: 8 }}>{p}</p>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* API */}
      <section id="api">
        <div className="wrap">
          <Reveal className="sec-head">
            <div className="eyebrow">Integrate in an afternoon</div>
            <h2>Three calls. Any stack.</h2>
            <p className="lede">Register a model, attest a decision, verify a certificate. That's the whole surface. The
              engine runs in your boundary; sensitive data never leaves.</p>
          </Reveal>

          <Reveal className="codewrap" style={{ marginTop: 24 }}>
            <div className="codetabs" role="tablist" aria-label="API examples">
              {["attest()", "verify()", "register_model()"].map((t, i) => (
                <button key={t} role="tab" aria-selected={tab === i} data-testid={`api-tab-${i}`} onClick={() => setTab(i)}>{t}</button>
              ))}
            </div>
            {tab === 0 && (
              <pre data-testid="api-code-0"><span className="cm"># Attest any AI decision — model is opaque, inputs/outputs are plain data</span>{"\n"}
<span className="kw">from</span> veriauth <span className="kw">import</span> <span className="nm">VeriAuth</span>{"\n"}
va = <span className="nm">VeriAuth</span>(api_key=<span className="st">"va_live_…"</span>){"\n\n"}
result = va.<span className="fn">attest</span>({"\n"}    model=risk_model,                 <span className="cm"># any framework, treated as opaque</span>{"\n"}    inputs={"{"}<span className="st">"features"</span>: features{"}"},   <span className="cm"># hashed, never stored raw</span>{"\n"}    output={"{"}<span className="st">"decision"</span>: <span className="st">"APPROVE"</span>, <span className="st">"confidence"</span>: 0.91{"}"},{"\n"}    use_case=<span className="st">"credit"</span>,              <span className="cm"># the only domain-specific field</span>{"\n"}){"\n"}
<span className="nm">print</span>(result.cert_id, result.signature)   <span className="cm"># signed, tamper-evident receipt</span></pre>
            )}
            {tab === 1 && (
              <pre data-testid="api-code-1"><span className="cm"># Anyone can verify — no access to your systems, no trust in you</span>{"\n"}
check = va.<span className="fn">verify</span>(cert_id){"\n\n"}
check.authentic      <span className="cm"># True — signature + input/output hashes match</span>{"\n"}
check.chain_intact   <span className="cm"># True — append-only log is unbroken</span>{"\n"}
check.model          <span className="cm"># risk_model · v3.2 · APPROVED at decision time</span></pre>
            )}
            {tab === 2 && (
              <pre data-testid="api-code-2"><span className="cm"># Fingerprint a model once; every later decision binds to it</span>{"\n"}
model_id = va.<span className="fn">register_model</span>({"\n"}    model=risk_model,{"\n"}    name=<span className="st">"risk_model"</span>, version=<span className="st">"v3.2"</span>,{"\n"}    use_case=<span className="st">"credit"</span>,{"\n"})   <span className="cm"># SHA-256 fingerprint → registry → APPROVED / PENDING / REVOKED</span></pre>
            )}
          </Reveal>

          <div className="flowlabel">Drops into your existing agents &amp; pipelines</div>
          <Stagger className="integrations">
            {["LangChain", "CrewAI", "MCP", "OpenAI Agents SDK", "Google ADK", "PKCS#11 HSM / TEE", "REST · Python · gRPC"].map((i) => (
              <Item className="intg" key={i}><b>› </b>{i}</Item>
            ))}
          </Stagger>
          <p className="kv" style={{ marginTop: 14 }}>Teaser surface shown. Full spec, SDKs and self-host guide available on request.</p>
        </div>
      </section>

      {/* TEST */}
      <section id="test">
        <div className="wrap">
          <Reveal className="sec-head">
            <div className="eyebrow">Test your AI</div>
            <h2>Find your gaps before someone else does.</h2>
            <p className="lede">Two ways to see where your AI decisions are unprovable — pick the one that fits how much you
              want to share. Both run without sending us your code.</p>
          </Reveal>
          <Stagger className="grid g2" style={{ marginTop: 26 }}>
            <Item className="test-card">
              <span className="tag">NO ACCESS · ~2 MIN</span>
              <h3>Readiness self-assessment</h3>
              <p style={{ color: "var(--mut)", fontSize: 14, margin: 0 }}>Answer eight questions about how your AI decisions are
                recorded. Get a report mapping each gap to regulation and penalty exposure — instantly, in your browser.</p>
              <ul>
                <li>Nothing leaves your machine until you choose</li>
                <li>Statutory / advisory split shown honestly</li>
                <li>Downloadable report for your board</li>
              </ul>
              <div className="go"><a className="btn" href="/assessment" data-testid="test-assessment-btn"
                onClick={(e) => { e.preventDefault(); navigate("/assessment"); }}>Start the 2-minute test →</a></div>
            </Item>
            <Item className="test-card">
              <span className="tag">DEEPER · PASTE · ZIP · GIT / URL</span>
              <h3>VeriScan code scan</h3>
              <p style={{ color: "var(--mut)", fontSize: 14, margin: 0 }}>Point VeriScan at your code — paste it, upload a
                <code> .zip</code>, or give a public Git repo or file URL. It flags every place a decision or outcome runs
                without attestation, maps findings to clauses and penalties, and returns a preview instantly.</p>
              <ul>
                <li>Static, read-only — no code executed, scanned in memory</li>
                <li>Findings mapped to VeriAuth modules &amp; fixes</li>
                <li>You see a preview; the full report reaches our team</li>
              </ul>
              <div className="go"><a className="btn" href="/scan" data-testid="test-scan-btn"
                onClick={(e) => { e.preventDefault(); navigate("/scan"); }}>Run VeriScan →</a></div>
            </Item>
          </Stagger>
        </div>
      </section>
    </main>
  );
}
